import { FC, useEffect } from 'react';
import { navigate } from 'gatsby';
import classNames from 'classnames';
import { useMeQuery } from 'lib/home/generated/queries';
import { SubscriptionStatus } from 'lib/home/generated/types/api';
import { getQueryParams, openReplayTracker } from 'lib/util';
import { Container, Layout, LayoutProps } from 'lib/components';
import {
  RegistrationCheckoutInfo,
  RegistrationRouteMap,
  RegistrationStep,
  useActiveStep,
  useAuthStore,
  useRegistrationStore
} from '../../../store';
import { StepsIndicator } from '../../Steps';
import { Logo } from '../../Logo/Logo';

import './getting-started.scss';

export interface GettingStartedLayoutProps extends LayoutProps {
  step?: RegistrationStep;
  showSteps?: boolean;
}

export const GettingStartedLayout: FC<GettingStartedLayoutProps> = ({
  step,
  children,
  title,
  showSteps = true,
  className,
  ...props
}) => {
  const { checkoutInfo, setCheckoutInfo, preferences, setPreferences, selectedPlan, setSelectedPlan } =
    useRegistrationStore();
  const { token } = useAuthStore();

  const queryParams = getQueryParams();
  const params = {
    first_name: queryParams.get('firstName'),
    last_name: queryParams.get('lastName'),
    postal_code: queryParams.get('postalCode')
  };

  const { data, isError, refetch } = useMeQuery(undefined, {
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  const me = data?.me;
  const isAuthorized = !isError && !!me;

  useEffect(() => {
    if (!me || !(setCheckoutInfo && setPreferences && setSelectedPlan)) return;

    const first_name = params.first_name ?? checkoutInfo.first_name ?? me.first_name;
    const last_name = params.last_name ?? checkoutInfo.last_name ?? me.last_name;
    const postal_code = params.postal_code ?? checkoutInfo.postal_code ?? me.postal_code;
    const email = checkoutInfo.email ?? me.email ?? '';
    const allergens = preferences.allergens ?? me.meal_preferences?.allergens;
    const diets = preferences.diets ?? me.meal_preferences?.diets;

    // Note: ensure the initial required data exists before allowing the user to continue
    if (!me && (!first_name || !last_name || !postal_code)) {
      navigate('/getting-started');
      return;
    }

    openReplayTracker.setUserID(me.email);
    const updatedCheckoutInfo: RegistrationCheckoutInfo = { first_name, last_name, postal_code, email };
    const updatedPreferences = { allergens, diets };
    const plan = selectedPlan || me.subscription?.product_variant_id;
    setCheckoutInfo(updatedCheckoutInfo);
    setPreferences(updatedPreferences);
    setSelectedPlan(plan);
  }, [me, setCheckoutInfo, setPreferences, setSelectedPlan]);

  useEffect(() => {
    refetch();
  }, []);

  useActiveStep(step);

  const activeStep = useRegistrationStore(state => state.activeStep);

  const alreadyCompletedOnboarding =
    activeStep !== RegistrationStep.success &&
    isAuthorized &&
    me?.subscription &&
    me.subscription.status !== SubscriptionStatus.Onboarding;

  if (alreadyCompletedOnboarding && token) {
    navigate('/');
    return null;
  }

  const steps = [
    { key: RegistrationStep.index, title: 'Welcome' },
    {
      key: RegistrationStep.unsupportedPostalCode,
      title: `Sorry, we're not shipping there yet`,
      hasBackButton: true,
      hidden: true
    },
    {
      key: RegistrationStep.selectPlan,
      title: 'Select a plan',
      previousStep: RegistrationStep.index,
      hasBackButton: true
    },
    { key: RegistrationStep.preferences, title: `Personalize your box`, hasBackButton: true },
    { key: RegistrationStep.createAccount, title: `Great, let's set up an account`, hasBackButton: true },
    { key: RegistrationStep.checkEmail, title: `Email sent!`, hidden: true },
    {
      key: RegistrationStep.customizeBox,
      title: 'Customize Box',
      hasBackButton: true,
      previousStep: RegistrationStep.preferences,
      showActive: 6 // note: having this set here prevents an animation when switching back from the swap meal page
    },
    {
      key: RegistrationStep.swapMeal,
      title: 'Swap Meal',
      hasBackButton: true,
      previousStep: RegistrationStep.customizeBox,
      hidden: true,
      showActive: 6
    },
    { key: RegistrationStep.orderSummary, title: 'Order Summary', hasBackButton: true },
    { key: RegistrationStep.success, title: 'Success', hidden: true }
  ];

  const findPreviousStep = () => {
    const activeStepIndex = steps.findIndex(({ key }) => key === activeStep);
    if (!steps[activeStepIndex] || !steps[activeStepIndex].hasBackButton) return;
    return steps[activeStepIndex].previousStep || steps[activeStepIndex - 1].key;
  };

  const handleBackClick = () => navigate(RegistrationRouteMap[findPreviousStep()]);

  title = title || steps.find(s => s.key === step)?.title;

  return (
    <Layout className={classNames('getting-started-layout min-h-screen flex flex-col', className)} {...props}>
      <div className="getting-started-layout__header">
        <Container>
          {showSteps && (
            <>
              <div className={classNames('h-16 pb-0 pt-4 sm:pb-6 sm:pt-16 flex justify-center items-center relative')}>
                <Logo />
              </div>

              <StepsIndicator
                className="mb-6 mt-4 sm:my-8"
                onBackClick={handleBackClick}
                steps={steps}
                activeStep={activeStep}
              />
            </>
          )}
        </Container>
      </div>

      <div className="flex-grow flex flex-col">{children}</div>
    </Layout>
  );
};
